<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import {
  APPOINTMENT_ATTRIBUTE_KEYS,
  appointmentFeaturesForAccount,
} from '../appointmentFields';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useAlert } from 'dashboard/composables';
import CustomAttribute from 'dashboard/components/CustomAttribute.vue';

const props = defineProps({
  customAttributes: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['update', 'delete']);

const { t } = useI18n();
const getters = useStoreGetters();
const { accountId } = useAccount();
const { isAdmin } = useAdmin();

const attributeDefinitions = computed(() =>
  getters['attributes/getAttributesByModel'].value('task_attribute')
);

const attributes = computed(() => {
  const attrs = props.customAttributes || {};
  return attributeDefinitions.value
    .filter(
      attr =>
        !appointmentFeaturesForAccount(accountId.value).enabled ||
        !APPOINTMENT_ATTRIBUTE_KEYS.has(attr.attribute_key)
    )
    .map(attr => ({
      ...attr,
      value: attr.attribute_key in attrs ? attrs[attr.attribute_key] : '',
    }));
});

const onUpdate = (key, value) => emit('update', key, value);
const onDelete = key => emit('delete', key);
const onCopy = async value => {
  await copyTextToClipboard(value);
  useAlert(t('COMPONENTS.CODE.COPY_SUCCESSFUL'));
};
</script>

<template>
  <div class="flex flex-col">
    <CustomAttribute
      v-for="attr in attributes"
      :key="attr.id"
      :attribute-key="attr.attribute_key"
      :attribute-type="attr.attribute_display_type"
      :values="attr.attribute_values"
      :label="attr.attribute_display_name"
      :description="attr.attribute_description"
      :value="attr.value"
      show-actions
      :attribute-regex="attr.regex_pattern"
      :regex-cue="attr.regex_cue"
      @update="onUpdate"
      @delete="onDelete"
      @copy="onCopy"
    />
    <p v-if="!attributes.length" class="px-4 py-2 text-sm text-n-slate-11">
      {{ t('KANBAN.CUSTOM_ATTRIBUTES.NO_RECORDS_FOUND') }}
      <router-link
        v-if="isAdmin"
        :to="{
          name: 'attributes_list',
          params: { accountId },
        }"
        class="text-woot-500 hover:underline"
      >
        {{ t('KANBAN.CUSTOM_ATTRIBUTES.CREATE_LINK') }}
      </router-link>
    </p>
  </div>
</template>
